import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  RadioGroup,
  Stack,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  Chip,
  Button,
  Slide,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel as MuiFormControlLabel
} from "@mui/material";
import { Close, Add, ArrowBack } from "@mui/icons-material";
import { postData, getData, deleteData } from '../../Webservice/Webservice';
import { MdDeleteForever } from "react-icons/md";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const AddressDialog = ({
  open,
  onClose,
  selectedAddress,
  onAddressChange,
  onSave,
  onAddNewSave,
  isMobile = false,
  title = "Saved Address",
  saveButtonText = "Save Address",
  addNewButtonText = "Add New Address"
}) => {
  const [mode, setMode] = useState("list"); // 'list' or 'form'
  const [addresses, setAddresses] = useState([]);
  const [internalSelectedAddress, setInternalSelectedAddress] = useState(selectedAddress);

  // Delete Confirmation State
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    type: "home",
    name: "",
    mobile: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    isDefault: true
  });

  const fetchAddresses = useCallback(async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      const response = await getData('address/list', {}, token);
      if (response && response.addresses) {
        setAddresses(response.addresses);
        // If no address is selected, select the default one or the first one
        if (!internalSelectedAddress && response.addresses.length > 0) {
          const defaultAddr = response.addresses.find(addr => addr.isDefault);
          setInternalSelectedAddress(defaultAddr ? defaultAddr._id : response.addresses[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  }, [internalSelectedAddress]);

  useEffect(() => {
    if (open) {
      fetchAddresses();
    }
  }, [open, fetchAddresses]);

  // Update selected address when prop changes
  useEffect(() => {
    if (selectedAddress) {
      setInternalSelectedAddress(selectedAddress);
    }
  }, [selectedAddress]);

  const handleAddressChange = (event) => {
    const newAddress = event.target.value;
    setInternalSelectedAddress(newAddress);
    if (onAddressChange) {
      onAddressChange(event);
    }
  };

  const handleSave = () => {
    if (onSave && internalSelectedAddress) {
      const selected = addresses.find(addr => addr._id === internalSelectedAddress);
      onSave(selected);
    }
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveNewAddress = async () => {
    if (!formData.name || !formData.mobile || !formData.addressLine1 || !formData.city || !formData.pincode) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (!token) {
        // Handle no token case
        alert("Please sign in to add an address.");
        return;
      }

      const payload = {
        type: formData.type,
        name: formData.name,
        mobile: formData.mobile,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        landmark: formData.landmark,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country,
        isDefault: formData.isDefault.toString()
      };

      await postData('address/add', payload, token);

      if (onAddNewSave) {
        onAddNewSave();
      }

      // Refresh address list
      await fetchAddresses();

      // Reset and go back to list
      setMode("list");
      setFormData({
        type: "home",
        name: "",
        mobile: "",
        addressLine1: "",
        addressLine2: "",
        landmark: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
        isDefault: true
      });
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  const handleBackToList = () => {
    setMode("list");
  };

  const handleDeleteClick = (e, addressId) => {
    e.stopPropagation();
    setAddressToDelete(addressId);
    setDeleteConfirmationOpen(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setAddressToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!addressToDelete) return;

    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (!token) return;

      await deleteData(`address/delete/${addressToDelete}`, {}, token);

      // Refresh list
      await fetchAddresses();

      // If deleted address was selected, clear selection
      if (internalSelectedAddress === addressToDelete) {
        setInternalSelectedAddress(null);
      }

      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  return (<>
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
          ...(isMobile ? {} : {
            width: '420px',
            maxWidth: '420px',
            position: 'fixed',
            right: 0,
            top: 0,
            height: '100vh',
          })
        }
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode === "form" && (
              <IconButton onClick={handleBackToList}>
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {mode === "list" ? title : "Add New Address"}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {mode === "list" ? (
            <RadioGroup value={internalSelectedAddress} onChange={handleAddressChange}>
              <Stack spacing={2}>
                {addresses.map((address) => (
                  <Card
                    key={address._id}
                    variant="outlined"
                    sx={{
                      border: internalSelectedAddress === address._id ? '2px solid black' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      // '&:hover': { borderColor: 'black' }
                    }}
                  >
                    <CardContent sx={{ p: 2.5, position: 'relative' }}>
                      <FormControlLabel
                        value={address._id}
                        control={<Radio />}
                        label={
                          <Box sx={{ ml: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {address.name}
                              </Typography>
                              {address.type && (
                                <Chip label={address.type} size="small" variant="outlined" sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }} />
                              )}
                            </Box>

                            {address.mobile && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {address.mobile}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {address.addressLine1}
                              {address.addressLine2 ? `, ${address.addressLine2}` : ''}
                              {address.landmark ? `, ${address.landmark}` : ''}
                            </Typography>
                            <Typography variant="body2">
                              {address.city}, {address.state} {address.pincode}
                            </Typography>
                            <Typography variant="body2">{address.country}</Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                      />
                      <IconButton
                        onClick={(e) => handleDeleteClick(e, address._id)}
                        style={{
                          color: "red",
                          fontSize: "20px",
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          zIndex: 10
                        }}
                      >
                        <MdDeleteForever />
                      </IconButton>
                    </CardContent>
                  </Card>
                ))}

                {addresses.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <img src='/img/nolo.png' alt='noaddress' style={{ width: "150px", height: "150px" }} />
                    <Typography textAlign="center" color="text.secondary" >
                      No saved addresses
                    </Typography>
                  </Box>
                )}
              </Stack>
            </RadioGroup>
          ) : (
            // Add New Address Form
            <Stack spacing={2.5}>
              <FormControl fullWidth>
                <InputLabel>Address Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Address Type"
                >
                  <MenuItem value="home">Home</MenuItem>
                  <MenuItem value="office">Work</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Full Name *"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
              />

              <TextField
                label="Mobile Number *"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                fullWidth
                placeholder="+91 98765 43210"
              />

              <TextField
                label="Flat, House no., Building *"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                fullWidth
              />

              <TextField
                label="Area, Street, Sector, Village"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                fullWidth
              />

              <TextField
                label="Landmark (Optional)"
                name="landmark"
                value={formData.landmark}
                onChange={handleInputChange}
                fullWidth
                placeholder="E.g. near Apollo Hospital"
              />

              <TextField
                label="City *"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                fullWidth
              />

              <TextField
                label="State *"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                fullWidth
              />


              <TextField
                label="Pincode *"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ maxLength: 6 }}
              />

              <TextField
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                fullWidth
                disabled
              />

              <MuiFormControlLabel
                control={
                  <Checkbox
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                  />
                }
                label="Set as default address"
              />
            </Stack>
          )}
        </Box>

        {/* Footer Buttons */}
        <Stack spacing={2} sx={{ mt: 3 }}>
          {mode === "list" ? (
            <>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Add />}
                onClick={() => setMode("form")}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  border: '1px solid black',
                  color: 'black',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                {addNewButtonText}
              </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={handleSave}
                disabled={!internalSelectedAddress}
                sx={{
                  py: 1.5,
                  bgcolor: 'black',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'grey.900' }
                }}
              >
                {saveButtonText}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveNewAddress}
                sx={{
                  py: 1.5,
                  bgcolor: 'black',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'grey.900' }
                }}
              >
                Save New Address
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleBackToList}
                sx={{
                  py: 1.5,
                  border: '1px solid black',
                  color: 'black',
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteConfirmationOpen}
      onClose={handleCloseDeleteConfirmation}
      maxWidth="xs"
      fullWidth
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Confirm Deletion
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Are you sure you want to delete this address? This action cannot be undone.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            onClick={handleCloseDeleteConfirmation}
            sx={{
              color: 'black',
              borderColor: 'black',
              '&:hover': { borderColor: 'grey.800', bgcolor: 'grey.50' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            color="error"
            disableElevation
          >
            Delete
          </Button>
        </Stack>
      </Box>
    </Dialog>
  </>
  );
};

export default AddressDialog;